import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, Music, Users, User, ChevronRight, Menu, Home, MessageCircle, Search } from "lucide-react";
import { platformClient } from "@/lib/musicApi";
import { getSocket } from "@/lib/socket";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useTranslation } from "react-i18next";

/**
 * Collapsible overlay platform nav (Step 39a). Replaces the old fixed rail.
 * Desktop: a translucent icon-only rail that toggles visible/hidden and OVERLAYS
 * content (never pushes/resizes it). Tapping outside hides it without navigating.
 * Mobile: an icon-only bottom bar that auto-hides after 3s idle and slides back up
 * on any touch/scroll. No labels anywhere. Mounts for everyone on platform routes.
 */
const ACCENT = "var(--accent-primary)";
const ITEMS = [
  { to: "/watch", label: "Watch", labelKey: "nav.watch", Icon: PlayCircle },
  { to: "/music", label: "Music", labelKey: "nav.music", Icon: Music },
  { to: "/community", label: "Community", labelKey: "nav.community", Icon: Users },
  { to: "/profile", label: "Profile", labelKey: "nav.profile", Icon: User },
];

// Item 2: the global search trigger lives inside the platform nav (between Music and
// Community). It fires the same GlobalSearch overlay via a window event, so behaviour
// is identical to the old top-right icon on desktop and mobile.
const openGlobalSearch = () => window.dispatchEvent(new Event("open-global-search"));

const glass = {
  background: "rgba(17, 20, 34, 0.72)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const iconColor = (isActive) => (isActive ? ACCENT : "rgba(255,255,255,0.62)");

const TOKEN_KEY = "samtwo_platform_token";

const UnreadBadge = ({ count, testid }) =>
  count > 0 ? (
    <span
      data-testid={testid}
      className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
      style={{ background: "#ef4444", color: "#fff" }}
    >
      {count > 9 ? "9+" : count}
    </span>
  ) : null;

export const PlatformNav = () => {
  const { t } = useTranslation();
  const [railOpen, setRailOpen] = useState(false);
  const [barVisible, setBarVisible] = useState(true);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const idleTimer = useRef(null);
  const { isSubscriber, isDon, platformUser } = usePlatformAuth();
  const location = useLocation();
  const [dmUnread, setDmUnread] = useState(0);
  const isCreator = !!(platformUser && (platformUser.roles || []).some((r) => r === "artist" || r === "admin"));
  const dmTo = isCreator ? "/inbox" : "/messages";
  // Visible-but-locked: everyone sees the chat icon (guest/purchaser/former/Boss);
  // only currently-Don can actually read/send inside.
  const showDm = true;
  const canReadDm = isSubscriber && isDon;
  // Live ref so the socket handler always sees the current route without re-subscribing.
  const onMessagesRef = useRef(location.pathname === "/messages");
  onMessagesRef.current = location.pathname === "/messages";

  // Chat unread badge. The badge fires for EVERYONE as the retention pull, but
  // only a Don can fetch/clear the server count; non-Don users still get the
  // content-free live bump over the socket (no message body is pushed to them).
  useEffect(() => {
    let active = true;
    const socket = getSocket();
    const token = localStorage.getItem(TOKEN_KEY);
    const identify = () => { if (token) socket.emit("identify", { token }); };
    identify();
    socket.on("connect", identify);
    if (canReadDm) {
      platformClient.get("/dms/me/unread-count").then(({ data }) => { if (active) setDmUnread(data.count || 0); }).catch(() => {});
    }
    const onUnread = (p) => {
      if (canReadDm && onMessagesRef.current) {
        setDmUnread(0);
        platformClient.put("/dms/me/read").catch(() => {});
        return;
      }
      setDmUnread((p && p.count) || 0);
    };
    socket.on("dm-unread", onUnread);
    return () => { active = false; socket.off("dm-unread", onUnread); socket.off("connect", identify); };
  }, [canReadDm]);

  // Opening the conversation clears the badge (Don only; server resets sub_unread_count).
  useEffect(() => {
    if (canReadDm && location.pathname === "/messages") setDmUnread(0);
  }, [location.pathname, canReadDm]);


  // Desktop rail keeps its visible/hidden state across navigation (access-tiers.md):
  // selecting a section navigates but does NOT force the rail closed.

  // Mobile bottom bar: auto-hide after 3s idle, reappear on interaction.
  const poke = useCallback(() => {
    setBarVisible(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setBarVisible(false), 3000);
  }, []);

  useEffect(() => {
    poke();
    const opts = { passive: true };
    window.addEventListener("touchstart", poke, opts);
    window.addEventListener("scroll", poke, opts);
    window.addEventListener("mousemove", poke, opts);
    return () => {
      window.removeEventListener("touchstart", poke);
      window.removeEventListener("scroll", poke);
      window.removeEventListener("mousemove", poke);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [poke]);

  // Hide the mobile bar whenever a full-screen sheet (e.g. the Watch/Clips comment
  // sheet) is open, matching how Community already behaves. Overlays dispatch these.
  useEffect(() => {
    let depth = 0;
    const open = () => { depth += 1; setOverlayOpen(true); };
    const close = () => { depth = Math.max(0, depth - 1); if (depth === 0) setOverlayOpen(false); };
    window.addEventListener("samtwo:overlay-open", open);
    window.addEventListener("samtwo:overlay-close", close);
    return () => {
      window.removeEventListener("samtwo:overlay-open", open);
      window.removeEventListener("samtwo:overlay-close", close);
    };
  }, []);

  // Desktop nav-menu discoverability: fire the glow + tooltip nudge EVERY time a user
  // lands on / reloads any platform tab (guest/purchaser/subscriber/admin, desktop only).
  // No once-per-session gating; only wait out the intro splash so it isn't hidden.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    const onPlatform = /^\/(watch|music|community|profile|videos|messages|inbox|u\/)/.test(location.pathname);
    if (!onPlatform) { setPeek(false); return; }
    const fire = () => {
      if (sessionStorage.getItem("samtwo_splash_seen") !== "1") return false;
      setPeek(true);
      return true;
    };
    if (fire()) return;
    const iv = setInterval(() => { if (fire()) clearInterval(iv); }, 250);
    const stop = setTimeout(() => clearInterval(iv), 8000);
    return () => { clearInterval(iv); clearTimeout(stop); };
  }, [location.pathname]);

  useEffect(() => {
    if (!peek) return;
    const t = setTimeout(() => setPeek(false), 5000);
    return () => clearTimeout(t);
  }, [peek]);

  return (
    <>
      {/* Desktop discoverability nudge: rail peek slide + "Menu here" tooltip (fire together) */}
      <AnimatePresence>
        {peek && !railOpen && (
          <React.Fragment key="nav-nudge">
            <motion.div
              aria-hidden
              data-testid="platform-nav-peek"
              className="hidden md:flex flex-col items-center justify-center fixed left-0 top-0 h-full z-[64] pointer-events-none"
              style={{ ...glass, width: 64, borderRadius: "0 16px 16px 0" }}
              initial={{ x: -64 }}
              animate={{ x: [-64, -50, -50, -64] }}
              exit={{ x: -64 }}
              transition={{ duration: 1.1, times: [0, 0.25, 0.7, 1], ease: "easeInOut" }}
            >
              <ChevronRight size={18} style={{ color: "rgba(255,255,255,0.8)" }} />
            </motion.div>
            <motion.button
              data-testid="platform-nav-nudge-tooltip"
              onClick={() => setPeek(false)}
              className="hidden md:flex fixed left-8 top-[44px] -translate-y-1/2 z-[71] items-center px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{ background: "var(--accent-primary)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.25 }}
            >
              <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45" style={{ background: "var(--accent-primary)" }} />
              {t("nav.menuHere")}
            </motion.button>
          </React.Fragment>
        )}
      </AnimatePresence>
      {/* ---------- Desktop toggle + overlay rail ---------- */}
      <button
        onClick={() => { setRailOpen((v) => !v); setPeek(false); }}
        data-testid="platform-nav-toggle"
        className="hidden md:flex fixed left-0 top-4 z-[70] items-center gap-2 pl-2.5 pr-3.5 py-2.5 rounded-r-xl"
        style={{ ...glass, border: "1px solid var(--accent-primary)", color: "rgba(255,255,255,0.85)" }}
        aria-label={railOpen ? t("nav.closeMenu") : t("nav.openMenu")}
      >
        <Menu size={18} />
        <span className="text-xs font-semibold whitespace-nowrap">{railOpen ? t("nav.closeMenu") : t("nav.openMenu")}</span>
      </button>

      <AnimatePresence>
        {railOpen && (
          <>
            <motion.nav
              initial={{ x: -70, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -70, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 34 }}
              data-testid="platform-nav-rail"
              className="hidden md:flex flex-col items-center justify-center py-8 fixed left-0 top-0 h-full z-[66]"
              style={{ ...glass, width: 64, borderRadius: "0 16px 16px 0" }}
            >
              {/* Back to public site, set apart at the top */}
              <NavLink to="/" data-testid="rail-home" title={t("nav.backToSite")} className="mb-6">
                <motion.span whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }} className="flex">
                  <Home size={22} style={{ color: "rgba(255,255,255,0.62)" }} />
                </motion.span>
              </NavLink>
              <div className="w-7 h-px mb-6" style={{ background: "rgba(255,255,255,0.12)" }} />
              {/* Section icons, grouped */}
              <div className="flex flex-col items-center gap-7">
                {ITEMS.map(({ to, label, labelKey, Icon }) => (
                  <React.Fragment key={to}>
                    <NavLink to={to} data-testid={`rail-${label.toLowerCase()}`} title={t(labelKey)}>
                      {({ isActive }) => (
                        <motion.span whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }} className="flex">
                          <Icon size={24} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />
                        </motion.span>
                      )}
                    </NavLink>
                    {label === "Music" && (
                      <button onClick={openGlobalSearch} data-testid="rail-search" title={t("nav.search")} aria-label={t("nav.search")} className="flex">
                        <motion.span whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }} className="flex">
                          <Search size={24} style={{ color: "rgba(255,255,255,0.62)" }} />
                        </motion.span>
                      </button>
                    )}
                  </React.Fragment>
                ))}
                {showDm && (
                  <NavLink to={dmTo} data-testid="rail-messages" title={t("nav.messages")}>
                    {({ isActive }) => (
                      <motion.span whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }} className="relative flex">
                        <MessageCircle size={24} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />
                        {!isCreator && <UnreadBadge count={dmUnread} testid="rail-messages-badge" />}
                      </motion.span>
                    )}
                  </NavLink>
                )}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* ---------- Mobile auto-hiding bottom bar ---------- */}
      <motion.nav
        animate={{ y: overlayOpen ? 140 : 0 }}
        transition={{ type: "spring", stiffness: 360, damping: 32 }}
        data-testid="platform-nav-bottom"
        onTouchStart={poke}
        className="md:hidden fixed left-3 right-3 z-[66] flex items-center justify-around py-2.5 rounded-2xl"
        style={{ ...glass, bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)", pointerEvents: overlayOpen ? "none" : "auto" }}
      >
        {/* Mobile order: Watch, Music, DM (search moved to the top bar), Community, Profile,
            then Home (back to public site) on the far right. Desktop rail (ITEMS) is untouched. */}
        <NavLink to="/watch" data-testid="bottom-watch" className="p-2" title={t("nav.watch")}>
          {({ isActive }) => <PlayCircle size={26} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />}
        </NavLink>
        <NavLink to="/music" data-testid="bottom-music" className="p-2" title={t("nav.music")}>
          {({ isActive }) => <Music size={26} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />}
        </NavLink>
        {showDm && (
          <NavLink to={dmTo} data-testid="bottom-messages" className="p-2 relative" title={t("nav.messages")}>
            {({ isActive }) => (
              <span className="relative flex">
                <MessageCircle size={26} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />
                {!isCreator && <UnreadBadge count={dmUnread} testid="bottom-messages-badge" />}
              </span>
            )}
          </NavLink>
        )}
        <NavLink to="/community" data-testid="bottom-community" className="p-2" title={t("nav.community")}>
          {({ isActive }) => <Users size={26} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />}
        </NavLink>
        <NavLink to="/profile" data-testid="bottom-profile" className="p-2" title={t("nav.profile")}>
          {({ isActive }) => <User size={26} style={{ color: iconColor(isActive), fill: isActive ? "currentColor" : "transparent" }} />}
        </NavLink>
        <NavLink to="/" data-testid="bottom-home" className="p-2" title={t("nav.backToSite")}>
          <Home size={26} style={{ color: "rgba(255,255,255,0.62)" }} />
        </NavLink>
      </motion.nav>
    </>
  );
};

export default PlatformNav;
